import React from 'react';
import { Search, X } from 'lucide-react';

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  id?: string;
  /** Extra classes applied to the wrapping div */
  containerClassName?: string;
  containerStyle?: React.CSSProperties;
  /** Extra classes applied to the <input> */
  inputClassName?: string;
  inputStyle?: React.CSSProperties;
  /** Color for the search/clear icons */
  iconColor?: string;
  /** Size of the search icon (default 18) */
  iconSize?: number;
  /**
   * When true, styles the input with hardcoded dark-mode colors instead of CSS
   * theme variables. Use this for elements that are always dark regardless of
   * the current light/dark theme (e.g. the side-menu popup).
   */
  forceDark?: boolean;
}

export function SearchInput({
  value,
  onChange,
  placeholder = 'Search...',
  id,
  containerClassName = '',
  containerStyle,
  inputClassName = '',
  inputStyle,
  iconColor,
  iconSize = 18,
  forceDark = false,
}: SearchInputProps) {
  const resolvedIconColor = iconColor ?? (forceDark ? '#9ca3af' : 'var(--color-text-secondary, #6b7280)');

  const baseInputClass = forceDark
    ? 'w-full rounded-lg text-sm outline-none border transition-colors leading-none'
    : 'w-full rounded-lg text-sm outline-none border transition-colors leading-none border-[var(--color-border)] bg-[var(--color-background)] text-[var(--color-text)]';

  const darkInputStyle: React.CSSProperties = forceDark
    ? {
        background: 'rgba(255,255,255,0.08)',
        border: '1px solid rgba(255,255,255,0.15)',
        color: '#e5e7eb',
        ...inputStyle,
      }
    : inputStyle ?? {};

  return (
    <div className={`relative ${containerClassName}`} style={containerStyle}>
      <Search
        size={iconSize}
        className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
        style={{ color: resolvedIconColor }}
      />
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`pl-8 pr-7 py-1.5 ${baseInputClass} ${inputClassName}`}
        style={darkInputStyle}
      />
      {value && (
        <button
          onClick={() => onChange('')}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-black/10 dark:hover:bg-white/10"
        >
          <X size={12} style={{ color: resolvedIconColor }} />
        </button>
      )}
    </div>
  );
}
